/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * The component for Money Transfer first step
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.2.0
 */

import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import type { IAccountSelectItem } from '@components/AccountSelect/AccountSelectTypes';
import { AccountSelect } from '@components/common';
import { LayoutStyled } from '@components/common/Block/Layout';
import { MoneyInput } from '@components/common/MoneyInput/MoneyInput';
import { parseMoneyToFloat } from '@components/common/MoneyInput/utils/parseMoneyToFloat';
import { DEBOUNCE_DEFAULT_DELAY } from '@modules/app/appConstants';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import type ICommonTranslation from '@modules/internationalization/types/common';
import type { IMoneyTransferTranslation } from '@modules/internationalization/types/wizards/moneyTransfer';
import type { IRootReducer } from '@reducers/rootReducer';
import { formatBalance } from '@utils/formatText';
import { stringFormat } from '@utils/stringFormat';

import {
  moneyTransferCheckRecipientsStart,
  moneyTransferClearSearchValue,
  moneyTransferLoadAccountList,
  moneyTransferSetAccount,
  moneyTransferSetAmount,
  moneyTransferSetSearchValue,
  moneyTransferStorageSaveStep,
  moneyTransferValidateStart,
} from '../moneyTransferActions';
import {
  makeSelectMoneyTransferAccountErrorCode,
  makeSelectMoneyTransferAccountList,
  makeSelectMoneyTransferAmount,
  makeSelectMoneyTransferAmountErrorCode,
  makeSelectMoneyTransferCheckRecipientsLoading,
  makeSelectMoneyTransferHasMore,
  makeSelectMoneyTransferIsLoading,
  makeSelectMoneyTransferSearchValue,
  makeSelectMoneyTransferSelectedAccount,
} from '../moneyTransferSelectors';
import type {
  IMoneyTransferClearSearchValuePayload,
  IMoneyTransferLoadAccountListPayload,
  IMoneyTransferSetAccountPayload,
  IMoneyTransferSetSearchValuePayload,
} from '../types/moneyTransferActionsTypes';
import { EMoneyTransferParamType } from '../types/moneyTransferTypes';
import {
  MoneyTransferAmountContainerStyled,
  MoneyTransferFirstStepContainerStyled,
  MoneyTransferFirstStepParamsContainerStyled,
  MoneyTransferHintStyled,
} from './styled/MoneyTransferStyled';

export interface IMoneyTransferStepAccount {
  moneyTransferTranslation: IMoneyTransferTranslation;
}

interface IConnectedProps {
  accountErrorCodeFrom: string;
  accountErrorCodeTo: string;
  amountErrorCode: string;
  isCheckRecipientsLoading: boolean;
  isLoadingFrom?: boolean;
  isLoadingTo?: boolean;
  hasMoreFrom: boolean;
  hasMoreTo: boolean;
  listDataFrom: IAccountSelectItem[];
  listDataTo: IAccountSelectItem[];
  searchValueFrom: string;
  searchValueTo: string;
  selectedAccountFrom: IAccountSelectItem;
  selectedAccountTo: IAccountSelectItem;
  transferAmount: string;
  translationCommon: ICommonTranslation;
}

interface IDispatchProps {
  checkRecipients: () => void;
  clearSearchValue: (payload: IMoneyTransferClearSearchValuePayload) => void;
  loadAccountList: (payload: IMoneyTransferLoadAccountListPayload) => void;
  setSearchValue: (payload: IMoneyTransferSetSearchValuePayload) => void;
  setAccount: (payload: IMoneyTransferSetAccountPayload) => void;
  setTransferAmount: (payload: string) => void;
  storageSaveStep: () => void;
  validateStart: () => void;
}

export type TMoneyTransferStepAccount = IMoneyTransferStepAccount &
  IConnectedProps &
  IDispatchProps;

export const MoneyTransferStepAccount = (props: TMoneyTransferStepAccount) => {
  const handleOpenList = (paramType: EMoneyTransferParamType) => {
    const { number: accountNumber } =
      paramType === EMoneyTransferParamType.transferAccountFrom
        ? props.selectedAccountFrom
        : props.selectedAccountTo;

    props.setSearchValue({ paramType, searchValue: accountNumber });
    props.loadAccountList({ paramType, start: true });
  };

  const delayedProcessAmount = debounce(() => {
    props.validateStart();
    props.storageSaveStep();
  }, DEBOUNCE_DEFAULT_DELAY);

  const transferMaxAmountHint =
    stringFormat(
      props.moneyTransferTranslation.transferAmount.hint,
      formatBalance(props.selectedAccountFrom.balance),
    ) +
    ' ' +
    props.translationCommon.currency;

  const handleAmountChange = (value: string): void => {
    props.setTransferAmount(value);
    delayedProcessAmount();
  };

  const handleSetAccount = ({
    paramType,
    account,
  }: {
    paramType: EMoneyTransferParamType;
    account: IAccountSelectItem;
  }) => {
    props.setAccount({ paramType, account });
    if (paramType === EMoneyTransferParamType.transferAccountFrom)
      props.checkRecipients();
    props.validateStart();
    props.storageSaveStep();
  };

  const isAmountValid =
    (!props.transferAmount ||
      parseMoneyToFloat(props.transferAmount) <=
        props.selectedAccountFrom.balance) &&
    !props.amountErrorCode;

  const amountErrorCode =
    props.amountErrorCode &&
    props.moneyTransferTranslation.error.codes[props.amountErrorCode]
      ? props.moneyTransferTranslation.error.codes[props.amountErrorCode]
      : '';

  const isParamDisabled =
    !props.selectedAccountFrom.id ||
    !!props.accountErrorCodeFrom ||
    props.isCheckRecipientsLoading;

  const renderAccountSelect = (paramType: EMoneyTransferParamType) => {
    const accountSelectStateProps =
      paramType === EMoneyTransferParamType.transferAccountFrom
        ? {
            accountList: props.listDataFrom,
            selectedAccount: props.selectedAccountFrom,
            searchValue: props.searchValueFrom,
            label: props.moneyTransferTranslation.labelAccountFrom,
            isLoading: props.isLoadingFrom || props.isCheckRecipientsLoading,
            hasMore: props.hasMoreFrom,
          }
        : {
            accountList: props.listDataTo,
            selectedAccount: props.selectedAccountTo,
            searchValue: props.searchValueTo,
            label: props.moneyTransferTranslation.labelAccountTo,
            isLoading: props.isLoadingTo,
            hasMore: props.hasMoreTo,
          };

    const accountErrorCode =
      paramType === EMoneyTransferParamType.transferAccountFrom
        ? props.accountErrorCodeFrom
        : props.accountErrorCodeTo;

    const disabled: boolean =
      paramType === EMoneyTransferParamType.transferAccountTo &&
      isParamDisabled;

    return (
      <>
        <AccountSelect
          {...accountSelectStateProps}
          dataNameComponent={
            paramType === EMoneyTransferParamType.transferAccountTo
              ? 'accountSelectMoneyTransferTo'
              : 'accountSelectMoneyTransferFrom'
          }
          disabled={disabled}
          isValid={!accountErrorCode}
          selectPlaceholder={
            props.moneyTransferTranslation.accountSelectPlaceholder
          }
          setSearchValue={(value: string) =>
            props.setSearchValue({ paramType, searchValue: value })
          }
          translation={props.translationCommon}
          onAccountSelect={(account: IAccountSelectItem) =>
            handleSetAccount({ paramType, account })
          }
          onOpenList={() => handleOpenList(paramType)}
          onScrollAccountList={() => props.loadAccountList({ paramType })}
          onStartSearch={() =>
            props.loadAccountList({ paramType, start: true })
          }
        />
        {accountErrorCode &&
          props.moneyTransferTranslation.error.codes[accountErrorCode] && (
            <MoneyTransferHintStyled isValid={false}>
              {props.moneyTransferTranslation.error.codes[accountErrorCode]}
            </MoneyTransferHintStyled>
          )}
      </>
    );
  };

  return (
    <MoneyTransferFirstStepContainerStyled
      data-container={'money-transfer-first-step'}
    >
      <div
        dangerouslySetInnerHTML={{
          __html: props.moneyTransferTranslation.hintTextFirstStep,
        }}
      />

      <MoneyTransferFirstStepParamsContainerStyled>
        <LayoutStyled className="mb-4">
          {renderAccountSelect(EMoneyTransferParamType.transferAccountFrom)}
        </LayoutStyled>

        <LayoutStyled className="mb-4">
          {renderAccountSelect(EMoneyTransferParamType.transferAccountTo)}
        </LayoutStyled>

        <MoneyTransferAmountContainerStyled>
          <MoneyInput
            dataName={'amount'}
            disabled={isParamDisabled}
            isValid={isAmountValid}
            label={props.moneyTransferTranslation.transferAmount.label}
            value={props.transferAmount || ''}
            onMoneyInputChange={handleAmountChange}
          />
          {!isParamDisabled && (
            <MoneyTransferHintStyled isValid={isAmountValid}>
              {`${transferMaxAmountHint} ${amountErrorCode}`}
            </MoneyTransferHintStyled>
          )}
        </MoneyTransferAmountContainerStyled>
      </MoneyTransferFirstStepParamsContainerStyled>
    </MoneyTransferFirstStepContainerStyled>
  );
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>(
  {
    accountErrorCodeFrom: makeSelectMoneyTransferAccountErrorCode(
      EMoneyTransferParamType.transferAccountFrom,
    ),
    accountErrorCodeTo: makeSelectMoneyTransferAccountErrorCode(
      EMoneyTransferParamType.transferAccountTo,
    ),
    amountErrorCode: makeSelectMoneyTransferAmountErrorCode(),
    isCheckRecipientsLoading: makeSelectMoneyTransferCheckRecipientsLoading(),
    isLoadingFrom: makeSelectMoneyTransferIsLoading(
      EMoneyTransferParamType.transferAccountFrom,
    ),
    isLoadingTo: makeSelectMoneyTransferIsLoading(
      EMoneyTransferParamType.transferAccountTo,
    ),
    hasMoreFrom: makeSelectMoneyTransferHasMore(
      EMoneyTransferParamType.transferAccountFrom,
    ),
    hasMoreTo: makeSelectMoneyTransferHasMore(
      EMoneyTransferParamType.transferAccountTo,
    ),
    listDataFrom: makeSelectMoneyTransferAccountList(
      EMoneyTransferParamType.transferAccountFrom,
    ),
    listDataTo: makeSelectMoneyTransferAccountList(
      EMoneyTransferParamType.transferAccountTo,
    ),
    searchValueFrom: makeSelectMoneyTransferSearchValue(
      EMoneyTransferParamType.transferAccountFrom,
    ),
    searchValueTo: makeSelectMoneyTransferSearchValue(
      EMoneyTransferParamType.transferAccountTo,
    ),
    selectedAccountFrom: makeSelectMoneyTransferSelectedAccount(
      EMoneyTransferParamType.transferAccountFrom,
    ),
    selectedAccountTo: makeSelectMoneyTransferSelectedAccount(
      EMoneyTransferParamType.transferAccountTo,
    ),
    transferAmount: makeSelectMoneyTransferAmount(),
    translationCommon: makeSelectTranslationCommon(),
  },
);

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  checkRecipients: () => dispatch(moneyTransferCheckRecipientsStart()),
  clearSearchValue: (payload) =>
    dispatch(moneyTransferClearSearchValue(payload)),
  loadAccountList: (payload) => dispatch(moneyTransferLoadAccountList(payload)),
  setAccount: (payload) => dispatch(moneyTransferSetAccount(payload)),
  setSearchValue: (payload) => dispatch(moneyTransferSetSearchValue(payload)),
  setTransferAmount: (payload) => dispatch(moneyTransferSetAmount(payload)),
  storageSaveStep: () => dispatch(moneyTransferStorageSaveStep()),
  validateStart: () => dispatch(moneyTransferValidateStart()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MoneyTransferStepAccount);
